import { useMemo, useSyncExternalStore, useRef, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { EventId, PlanEvent } from "../../../../shared/types";
import type { RecurrenceToastState } from "./RecurrenceActionToast";
import type { EventFullData } from "../../../events/types/event-form-types";
import type { FormAnchor } from "../../hooks/use-week-calendar-state";
import { CATEGORY_LABELS, CATEGORY_BAR_COLORS } from "../../../../shared/types";
import { EventLinkedTasks } from "../../../events/components/event-item/EventLinkedTasks";
import { DailyChecklist } from "../../../events/components/DailyChecklist";
import { ImageGallery } from "../../../images/components/ImageGallery";
import { useEditingEventDelete } from "./useEditingEventDelete";
import { ModalActionButton } from "../../../../ui/molecules/ModalActions";
import * as taskStore from "../../../tasks/store/task-store";

const POPOVER_MAX_W_DESKTOP = 360;
const VIEWPORT_MARGIN = 20;
const GAP = 8;

function computePosition(anchor: FormAnchor, popoverH: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const isMobile = vw < 768;
  const POPOVER_MAX_W = isMobile ? vw - VIEWPORT_MARGIN * 2 : POPOVER_MAX_W_DESKTOP;

  let x = anchor.colRight + GAP;
  if (x + POPOVER_MAX_W + VIEWPORT_MARGIN > vw) {
    x = anchor.colLeft - POPOVER_MAX_W - GAP;
  }
  x = Math.max(VIEWPORT_MARGIN, Math.min(x, vw - POPOVER_MAX_W - VIEWPORT_MARGIN));

  let y = anchor.slotTop;
  if (popoverH > 0 && y + popoverH + VIEWPORT_MARGIN > vh) {
    y = vh - popoverH - VIEWPORT_MARGIN;
  }
  y = Math.max(VIEWPORT_MARGIN, y);

  return { left: x, top: y };
}

interface EventQuickViewModalProps {
  readonly event: PlanEvent;
  readonly events: PlanEvent[];
  readonly formAnchor?: FormAnchor | null;
  readonly onClose: () => void;
  readonly onDeleteEvent: (id: EventId) => void;
  readonly onSetEditingEvent: (event: PlanEvent | null) => void;
  readonly onOpenFullEdit: () => void;
  readonly onSetPendingResync: (event: PlanEvent | null) => void;
  readonly onSetRecurrenceToast: (toast: RecurrenceToastState | null) => void;
  readonly onDuplicate: (event: PlanEvent) => void;
  readonly onAddRecurrenceException?: (baseId: EventId, date: string) => void;
  readonly onUndoEvent?: () => void;
  readonly onAddEventFull?: (data: EventFullData) => void;
  readonly readOnly?: boolean;
}

export function EventQuickViewModal({
  event,
  events,
  formAnchor,
  onClose,
  onDeleteEvent,
  onSetEditingEvent,
  onOpenFullEdit,
  onSetPendingResync,
  onSetRecurrenceToast,
  onDuplicate,
  onAddRecurrenceException,
  onUndoEvent,
  onAddEventFull,
  readOnly,
}: EventQuickViewModalProps) {
  const allTasks = useSyncExternalStore(
    taskStore.subscribe,
    taskStore.getSnapshot,
  );

  const linkedTasks = useMemo(
    () => allTasks.filter((t) => t.eventId === event.id && !t.archived),
    [allTasks, event.id],
  );

  const { handleDelete } = useEditingEventDelete({
    events,
    onDeleteEvent,
    onSetEditingEvent,
    onSetRecurrenceToast,
    onAddRecurrenceException,
    onUndoEvent,
  });

  const hasSubtasks = (event.eventSubtasks?.length ?? 0) > 0;
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = () => {
    setIsDeleting(true);
    handleDelete(event.id);
    setTimeout(() => onClose(), 100);
  };

  const popoverRef = useRef<HTMLDivElement>(null);
  const initialStyle = useMemo(
    () => (formAnchor ? computePosition(formAnchor, 0) : {}),
    [formAnchor],
  );
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>(initialStyle);

  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!formAnchor || !el) return;
    const observer = new ResizeObserver(() => {
      setPopoverStyle(computePosition(formAnchor, el.offsetHeight));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [formAnchor]);

  const content = (
      <div
        className="bg-surface border border-border/50 rounded-xl shadow-2xl shadow-black/10 w-full max-w-sm md:max-w-xs max-h-[85vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-150 break-all flex flex-col"
      >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-surface/95 backdrop-blur-sm px-5 pt-4 pb-3 border-b border-border/30 shrink-0">
            <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${CATEGORY_BAR_COLORS[event.category]}`} />
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-ink leading-tight">
                  {event.title}
                </h2>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className="text-sm text-ink-muted font-medium">
                    {event.startTime} – {event.endTime === "24:00" ? "00:00" : event.endTime}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${CATEGORY_BAR_COLORS[event.category]} bg-opacity-10`}>
                    {CATEGORY_LABELS[event.category]}
                  </span>
                  {event.recurrence && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-ink-faint" title="Événement récurrent">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-ink-muted hover:text-ink hover:bg-surface-alt transition-colors"
                aria-label="Fermer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-5 py-4 space-y-5 flex-1 overflow-y-auto">
            {/* Daily checklist (event subtasks) */}
            {hasSubtasks && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-ink-faint uppercase tracking-wide">À faire</h3>
                <DailyChecklist
                  eventTitle={event.title}
                  subtasks={event.eventSubtasks ?? []}
                />
              </div>
            )}

            {/* Linked tasks */}
            <div className="space-y-2">
              <h3 className={`text-xs font-semibold uppercase tracking-wide ${readOnly ? "text-ink" : "text-ink-faint"}`}>Tâches liées</h3>
              <EventLinkedTasks
                eventId={event.id}
                eventCategory={event.category}
                eventDate={event.date}
                tasks={linkedTasks}
                readOnly={readOnly}
                onToggleTask={taskStore.toggleTaskDone}
                onAddTask={taskStore.addTaskForEvent}
                onUnlinkTask={(id) => taskStore.updateTask(id, { eventId: null })}
                onDeleteTask={taskStore.deleteTask}
              />
            </div>

            {/* Images */}
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-ink-faint uppercase tracking-wide">Images</h3>
              <ImageGallery entityType="event" entityId={event.id} hideUploader={readOnly} />
            </div>

            {/* Description preview */}
            {event.description && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-ink-faint uppercase tracking-wide">Description</h3>
                <div className="text-sm text-ink-muted leading-relaxed p-3 bg-surface-alt/50 rounded-lg">
                  {event.description}
                </div>
              </div>
            )}
          </div>

          {/* Actions - Footer standardisé */}
          {!readOnly && (
            <div className="px-5 pb-5 flex flex-col gap-2 border-t border-border/30 pt-4 shrink-0">
              {/* Bouton principal: Modifier */}
              <ModalActionButton
                variant="primary"
                onClick={onOpenFullEdit}
                className="w-full min-h-[44px]"
              >
                Modifier
              </ModalActionButton>

              {/* Actions secondaires en ligne */}
              <div className="flex gap-2">
                {onAddEventFull && (
                  <button
                    type="button"
                    onClick={() => { onDuplicate(event); onClose(); }}
                    className="flex-1 py-2.5 text-sm font-medium text-ink border border-border/50 rounded-lg hover:bg-surface-alt hover:border-border transition-colors min-h-[44px] touch-feedback"
                  >
                    Dupliquer
                  </button>
                )}
                {event.detachedFromId && (
                  <button
                    type="button"
                    onClick={() => onSetPendingResync(event)}
                    className="flex-1 py-2.5 text-sm font-medium text-accent border border-accent/30 rounded-lg hover:bg-accent/10 transition-colors min-h-[44px] touch-feedback"
                  >
                    Rattacher
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                  className="flex-1 py-2.5 text-sm font-medium text-danger border border-danger/30 rounded-lg hover:bg-danger/10 transition-colors disabled:opacity-50 min-h-[44px] touch-feedback"
                >
                  {isDeleting ? "Suppression..." : "Supprimer"}
                </button>
              </div>
            </div>
          )}
      </div>
  );

  if (formAnchor) {
    return createPortal(
      <>
        <div
          className="fixed inset-0 z-40 bg-black/40"
          onClick={onClose}
        />
        <div
          ref={popoverRef}
          className="fixed z-50 w-full max-w-xs md:max-w-xs"
          style={popoverStyle}
          data-ph-no-capture
        >
          {content}
        </div>
      </>,
      document.body,
    );
  }

  // Fallback: centered overlay
  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-4 pointer-events-none" data-ph-no-capture>
        <div className="pointer-events-auto w-full max-w-sm animate-scale-in">
          {content}
        </div>
      </div>
    </>,
    document.body,
  );
}
